import { Injectable } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AbstractListViewService } from '@y42/core/list-view';
import { DataTestType, SchemaTestForm, TestResultStatus } from '@y42/data-test';
import { DateTimeService } from '@y42/date-time';
import { UnwrapArray } from '@y42/typescript';
import { isNullish } from '@y42/util/nullish';
import { GetQuickFilterTextParams, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import isNumber from 'lodash/isNumber';
import { BehaviorSubject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { DataTestColumnsCellRendererInputValue } from '../components/cell-renderers/data-test-columns-cell-renderer/data-test-columns-cell-renderer.component';
import { DataTestConditionCellRendererInputParam } from '../components/cell-renderers/data-test-condition-cell-renderer/data-test-condition-cell-renderer.component';
import { JobWithDataTestsUIModel } from '../model/job.model';

type DataTestJobGridHelperBaseModel = Pick<JobWithDataTestsUIModel, 'request_id'> & {
  id: JobWithDataTestsUIModel['request_id'];
  test: UnwrapArray<JobWithDataTestsUIModel['tests']>;
};

@UntilDestroy()
@Injectable()
export class DataTestJobGridHelperService {
  constructor(
    private readonly dateTimeService: DateTimeService,
    private readonly dataTestJobOverviewListViewService: AbstractListViewService<DataTestJobGridHelperBaseModel>,
  ) {
    this.updateGridDataSchemaTestOnGridDataChanged();
  }

  /**
   * stores schema tests for all request_ids to render 'columns' and 'condition' columns properly
   */
  private readonly gridDataSchemaTests$$ = new BehaviorSubject<
    Array<{ requestId: string; schemaTest: SchemaTestForm }>
  >([]);

  readonly conditionValueGetter = <T extends DataTestJobGridHelperBaseModel>(params: ValueGetterParams<T>) =>
    this.getSchemaTestDependentCellRendererParams(params);
  readonly columnsValueGetter = <T extends DataTestJobGridHelperBaseModel>(params: ValueGetterParams<T>) =>
    this.getSchemaTestDependentCellRendererParams(params);

  readonly durationValueGetter = <T extends DataTestJobGridHelperBaseModel>(params: ValueGetterParams<T>) => {
    const testResult = params.data?.test.result ?? null;
    if (isNullish(testResult)) {
      return 0;
    }
    const { start_time, end_time } = testResult;
    return this.dateTimeService.diff(start_time, end_time, 'milliseconds');
  };

  readonly durationValueFormatter = <T extends DataTestJobGridHelperBaseModel>(
    params: ValueFormatterParams<T, { value: number } | number | null>,
  ) => {
    let value: number | null = null;
    if (isNullish(params.value)) {
      value = null;
    } else if (isNumber(params.value)) {
      value = params.value;
    } else if (typeof params.value === 'object' && 'value' in params.value) {
      value = params.value.value;
    }

    if (isNullish(value)) {
      return '-';
    }

    const formattedDuration = this.dateTimeService.formatDuration(value, { unit: 'milliseconds' });
    // if duration is too little then formattedDuration equals an empty string
    return formattedDuration === '' ? '0.05 seconds' : formattedDuration;
  };

  readonly dataTestStatusValueFormatter = <T extends DataTestJobGridHelperBaseModel>(
    params: ValueFormatterParams<T, TestResultStatus> | GetQuickFilterTextParams<T, TestResultStatus>,
  ) => {
    const value = params.value;
    switch (value) {
      case TestResultStatus.Success:
        return 'Success';
      case TestResultStatus.Error:
        return 'Error';
      default:
        return value as string;
    }
  };

  private getSchemaTestDependentCellRendererParams<T extends DataTestJobGridHelperBaseModel>(
    params: ValueGetterParams<T>,
  ): DataTestColumnsCellRendererInputValue | DataTestConditionCellRendererInputParam {
    const result: DataTestColumnsCellRendererInputValue | DataTestConditionCellRendererInputParam = {
      dataTest: params.data?.test.request ?? null,
      schemaTest:
        this.gridDataSchemaTests$$.value.find((tests) => tests.requestId === params.data?.request_id)?.schemaTest ??
        null,
    };

    return result;
  }

  private updateGridDataSchemaTestOnGridDataChanged(): void {
    this.dataTestJobOverviewListViewService.data$
      .pipe(
        map((dataTests): Array<{ requestId: string; schemaTest: SchemaTestForm }> => {
          const result = dataTests
            .filter((dataItem) => dataItem.test.request.type === DataTestType.Schema)
            .map((schemaTest): { requestId: string; schemaTest: SchemaTestForm } => {
              return {
                requestId: schemaTest.request_id,
                schemaTest: schemaTest.test.request as SchemaTestForm,
              };
            });
          return result;
        }),
        tap((value) => this.gridDataSchemaTests$$.next(value)),
        untilDestroyed(this),
      )
      .subscribe();
  }
}
